import type { Rol, Usuario } from '@eventos-cien/shared';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  EmailAuthProvider,
  onAuthStateChanged,
  reauthenticateWithCredential,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signOut as fbSignOut,
  updatePassword,
  type User,
} from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { api } from '../lib/api';
import { apiAuth } from '../lib/apiAuth';
import { auth, db } from '../lib/firebase';

export interface UsuarioConId extends Usuario {
  uid: string;
}

interface ClaimsStaffPorCodigo {
  staffEventoId: string;
  staffEtiqueta: string;
}

function claimsStaffDe(claims: Record<string, unknown>): ClaimsStaffPorCodigo | null {
  if (typeof claims.staffEventoId !== 'string') return null;
  return {
    staffEventoId: claims.staffEventoId,
    staffEtiqueta: typeof claims.staffEtiqueta === 'string' ? claims.staffEtiqueta : 'Staff',
  };
}

interface EstadoAuth {
  /** Cargando el estado inicial de sesion + documento de usuario. */
  cargando: boolean;
  user: User | null;
  /** Documento usuarios/{uid}, o perfil sintetico para staff sin cuenta. Null si no tiene alta. */
  usuario: UsuarioConId | null;
  /** eventoId fijo de una sesion de staff sin cuenta (login por codigo), o null en cualquier otro caso. */
  staffEventoId: string | null;
  tieneRol: (...roles: Rol[]) => boolean;
  iniciarSesion: (email: string, password: string) => Promise<void>;
  /** Login de staff sin cuenta: valida el codigo contra la API y entra con el custom token resultante. */
  iniciarSesionConCodigo: (codigo: string) => Promise<void>;
  cerrarSesion: () => Promise<void>;
  /** Cambio de contraseña autoservicio: reautentica con la actual antes de fijar la nueva. */
  cambiarPassword: (actual: string, nueva: string) => Promise<void>;
}

const AuthContext = createContext<EstadoAuth | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [cargando, setCargando] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [usuario, setUsuario] = useState<UsuarioConId | null>(null);
  const [staffClaims, setStaffClaims] = useState<ClaimsStaffPorCodigo | null>(null);

  // Escucha el estado de sesion de Firebase Auth.
  useEffect(() => {
    return onAuthStateChanged(auth, (u) => {
      setUser(u);
      if (!u) {
        setUsuario(null);
        setStaffClaims(null);
        setCargando(false);
      }
    });
  }, []);

  // Sesion de staff sin cuenta: los claims vienen en el custom token, no hay
  // usuarios/{uid} que escuchar. Cualquier otra sesion sigue el camino
  // normal (documento usuarios/{uid} en vivo).
  useEffect(() => {
    if (!user) return;
    setCargando(true);
    let vivo = true;

    user
      .getIdTokenResult()
      .then((r) => {
        if (!vivo) return;
        const staff = claimsStaffDe(r.claims);
        setStaffClaims(staff);
        if (staff) {
          setUsuario({
            uid: user.uid,
            nombre: staff.staffEtiqueta,
            email: '',
            activo: true,
            roles: ['staff'],
            creadoEn: '',
          });
          setCargando(false);
          return undefined;
        }

        // Cuenta de Auth valida sin usuarios/{uid} (huerfana: borrada por
        // error, o creada a mano en la consola): se autoprovisiona una vez
        // con rol basico via el backend; el propio listener recoge el alta
        // en cuanto el documento exista, sin necesidad de recargar.
        let altaIntentada = false;
        const ref = doc(db, 'usuarios', user.uid);
        return onSnapshot(
          ref,
          (snap) => {
            if (snap.exists()) {
              setUsuario({ uid: user.uid, ...(snap.data() as Usuario) });
              setCargando(false);
              return;
            }
            setUsuario(null);
            setCargando(false);
            if (!altaIntentada) {
              altaIntentada = true;
              void apiAuth('/mi-alta', { method: 'POST' }).catch(() => {});
            }
          },
          () => setCargando(false),
        );
      })
      .catch(() => setCargando(false));

    return () => {
      vivo = false;
    };
  }, [user]);

  const valor = useMemo<EstadoAuth>(
    () => ({
      cargando,
      user,
      usuario,
      staffEventoId: staffClaims?.staffEventoId ?? null,
      tieneRol: (...roles: Rol[]) =>
        !!usuario?.activo && roles.some((r) => usuario.roles.includes(r)),
      iniciarSesion: async (email, password) => {
        await signInWithEmailAndPassword(auth, email.trim(), password);
      },
      iniciarSesionConCodigo: async (codigo) => {
        const { customToken } = await api<{ customToken: string }>('/staff/entrar', {
          method: 'POST',
          body: JSON.stringify({ codigo: codigo.trim() }),
        });
        await signInWithCustomToken(auth, customToken);
      },
      cerrarSesion: async () => {
        await fbSignOut(auth);
      },
      cambiarPassword: async (actual, nueva) => {
        if (!auth.currentUser?.email) throw new Error('No hay sesión activa');
        const credencial = EmailAuthProvider.credential(auth.currentUser.email, actual);
        await reauthenticateWithCredential(auth.currentUser, credencial);
        await updatePassword(auth.currentUser, nueva);
      },
    }),
    [cargando, user, usuario, staffClaims],
  );

  return <AuthContext.Provider value={valor}>{children}</AuthContext.Provider>;
}

export function useAuth(): EstadoAuth {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de <AuthProvider>');
  return ctx;
}
